import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import CmruShell from "../../layouts/CmruShell";
import { appModules } from "../../components/global";
import { GET, POST, PUT, DELETE, loading, alert, Select, XLSX, format_date } from "../../components/CustomComponent";
import { parseClassListRows } from "./classListParser";
import AttendanceTab from "./AttendanceTab";
import ScoreTab from "./ScoreTab";
import NicknameCell from "./NicknameCell";
import KpiStrip from "./KpiStrip";

const API = "v1/cmru/class-sections";

// เครื่องที่ตั้ง "ลดการเคลื่อนไหว": bootstrap ตัด transition ของ modal ทิ้ง ทำให้บางจังหวะ react-bootstrap ไม่ได้รับสัญญาณว่าปิดเสร็จ
// modal จะค้างแบบมองไม่เห็นและบังการกดทั้งหน้า จึงปิดแอนิเมชันไปเลย (ถอด modal ออกทันที) ซึ่งตรงกับความต้องการของผู้ใช้กลุ่มนี้อยู่แล้ว
const modalAnimation = !(typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);
const LIST_PATH = "/cmru/class-lists";

const sectionFormDefaults = {
  uid: "",
  academic_year: "",
  semester: "",
  campus: "",
  program_type: "",
  course_code: "",
  course_name: "",
  credits: "",
  section_no: "",
  instructor_name: "",
  class_schedule: "",
  midterm_exam: "",
  final_exam: "",
  note: "",
};

const studentFormDefaults = {
  enrollment_uid: "",
  seq_no: "",
  student_code: "",
  prefix_name: "",
  first_name: "",
  last_name: "",
  student_group: "",
  nickname: "",
  note: "",
};

const prefixOptions = ["นาย", "นางสาว", "นาง"].map((value) => ({ value, label: value }));
const semesterOptions = ["1", "2", "3"].map((value) => ({ value, label: `ภาคการศึกษา ${value}` }));

const getStudentName = (student) => [student?.prefix_name, [student?.first_name, student?.last_name].filter(Boolean).join(" ")].filter(Boolean).join("") || "-";

// เพศดูจากคำนำหน้า ใช้แยกโทนสีของวงกลมหน้าชื่อและนับชาย/หญิง คำนำหน้าอื่น (เช่น ยศ) = ไม่ระบุ
const genderIcons = { male: "fa-solid fa-mars", female: "fa-solid fa-venus", unknown: "fa-solid fa-user" };
const getGender = (student) => (student?.prefix_name === "นาย" ? "male" : ["นางสาว", "นาง"].includes(student?.prefix_name) ? "female" : "unknown");

// สีประจำวันแบบไทย (จันทร์เหลือง อังคารชมพู ...) แต้มไว้หน้าวันเวลาเรียน ให้กวาดตาหาวันสอนได้เร็ว
const dayTones = { "จ": "mon", "อ": "tue", "พ": "wed", "พฤ": "thu", "ศ": "fri", "ส": "sat", "อา": "sun" };

// แยก "อ. 08:30-11:30 A307" เป็น วัน / เวลา / ห้อง ถ้ารูปแบบไม่ตรงจะคืนข้อความเดิมทั้งก้อนใน time
const splitSchedule = (value) => {
  const raw = String(value || "").trim();
  const dayMatch = raw.match(/^(อา|พฤ|จ|อ|พ|ศ|ส)\.?\s+(.+)$/);
  const rest = dayMatch ? dayMatch[2] : raw;
  const timeMatch = rest.match(/^(\d{1,2}[:.]\d{2}\s*-\s*\d{1,2}[:.]\d{2})\s*(.*)$/);
  return {
    day: dayMatch ? dayMatch[1] : "",
    tone: dayMatch ? dayTones[dayMatch[1]] : "",
    time: timeMatch ? timeMatch[1] : rest,
    room: timeMatch ? timeMatch[2] : "",
  };
};

// ตัวอักษรย่อ + โทนสีของผู้สอน: ตัดตำแหน่งทางวิชาการ (ผศ.ดร. / อ. / อาจารย์) ออกก่อน แล้วหยิบพยัญชนะตัวแรก
// โทนสีคิดจากชื่อ คนเดิมจึงได้สีเดิมทุกแถว
const getInstructorBadge = (name) => {
  const bare = String(name || "").trim().replace(/^([^\s.]+\.\s*)+/, "").replace(/^(อาจารย์|นางสาว|นาง|นาย)\s*/, "");
  const initial = (bare.match(/[ก-ฮA-Za-z]/) || ["?"])[0].toUpperCase();
  const tone = [...bare].reduce((sum, char) => sum + char.charCodeAt(0), 0) % 5;
  return { initial, tone };
};

// ฟิลด์หัวกลุ่มเรียนที่ส่งเข้า API (DTO เป็น strict schema ส่งเกินไม่ได้)
const toSectionBody = (form) => ({
  academic_year: String(form.academic_year).trim(),
  semester: String(form.semester).trim(),
  campus: form.campus || "",
  program_type: form.program_type || "",
  course_code: form.course_code.trim(),
  course_name: form.course_name.trim(),
  credits: form.credits || "",
  section_no: String(form.section_no).trim(),
  instructor_name: form.instructor_name || "",
  class_schedule: form.class_schedule || "",
  midterm_exam: form.midterm_exam || "",
  final_exam: form.final_exam || "",
  note: form.note || "",
});

const validateSection = (form) => {
  const required = [["academic_year", "ปีการศึกษา"], ["semester", "ภาคการศึกษา"], ["course_code", "รหัสวิชา"], ["course_name", "ชื่อวิชา"], ["section_no", "Sec"]];
  const missing = required.find(([field]) => !String(form[field] ?? "").trim());
  if (missing) return `กรุณากรอก${missing[1]}`;
  if (!/^\d{4}$/.test(String(form.academic_year).trim())) return "ปีการศึกษาต้องเป็น พ.ศ. 4 หลัก เช่น 2569";
  return "";
};

const ModalHeader = ({ eyebrow, title, subtitle }) => (
  <Modal.Header closeButton>
    <div>
      <p className="stg-eyebrow">{eyebrow}</p>
      <Modal.Title>{title}</Modal.Title>
      {subtitle ? <p className="stg-modal-sub">{subtitle}</p> : null}
    </div>
  </Modal.Header>
);

const ModalFooter = ({ onHide, onSave, saveLabel = "บันทึกข้อมูล", saveIcon = "fa-solid fa-floppy-disk", disabled = false }) => (
  <Modal.Footer>
    <div className="stg-modal-actions">
      <button type="button" className="btn stg-btn stg-btn--ghost" onClick={onHide}>ยกเลิก</button>
      <button type="button" className="btn stg-btn stg-btn--primary" onClick={onSave} disabled={disabled}>
        <i className={saveIcon} />{saveLabel}
      </button>
    </div>
  </Modal.Footer>
);

// ช่องกรอกหัวกลุ่มเรียน ใช้ทั้งใน modal สร้าง/แก้ไข และในหน้าตรวจสอบก่อนนำเข้า
const SectionFields = ({ form, onChange }) => {
  const handleInput = (event) => onChange(event.target.name, event.target.value);

  return (
    <div className="row g-3">
      <div className="col-6 col-md-3">
        <label className="stg-label">ปีการศึกษา (พ.ศ.)</label>
        <input type="text" inputMode="numeric" name="academic_year" className="form-control" value={form.academic_year} onChange={handleInput} placeholder="2569" />
      </div>
      <div className="col-6 col-md-3">
        <label className="stg-label">ภาคการศึกษา</label>
        <Select className="form-control" value={String(form.semester || "")} options={semesterOptions} placeholder="เลือกภาค" onChange={(value) => onChange("semester", value)} />
      </div>
      <div className="col-md-2">
        <label className="stg-label">วิทยาเขต</label>
        <input type="text" name="campus" className="form-control" value={form.campus || ""} onChange={handleInput} placeholder="แม่ริม" />
      </div>
      <div className="col-md-4">
        <label className="stg-label">ระดับ / ประเภทนักศึกษา</label>
        <input type="text" name="program_type" className="form-control" value={form.program_type || ""} onChange={handleInput} placeholder="ปริญญาตรี (4 ปี) ภาคปกติ" />
      </div>
      <div className="col-md-3">
        <label className="stg-label">รหัสวิชา</label>
        <input type="text" name="course_code" className="form-control" value={form.course_code} onChange={handleInput} placeholder="THED1101" />
      </div>
      <div className="col-md-5">
        <label className="stg-label">ชื่อวิชา</label>
        <input type="text" name="course_name" className="form-control" value={form.course_name} onChange={handleInput} />
      </div>
      <div className="col-6 col-md-2">
        <label className="stg-label">หน่วยกิต</label>
        <input type="text" name="credits" className="form-control" value={form.credits || ""} onChange={handleInput} placeholder="3(3-0-6)" />
      </div>
      <div className="col-6 col-md-2">
        <label className="stg-label">Sec</label>
        <input type="text" name="section_no" className="form-control" value={form.section_no} onChange={handleInput} placeholder="53" />
      </div>
      <div className="col-md-6">
        <label className="stg-label">ผู้สอน</label>
        <input type="text" name="instructor_name" className="form-control" value={form.instructor_name || ""} onChange={handleInput} />
      </div>
      <div className="col-md-6">
        <label className="stg-label">วันเวลาเรียน / ห้อง</label>
        <input type="text" name="class_schedule" className="form-control" value={form.class_schedule || ""} onChange={handleInput} placeholder="อ. 08:30-11:30 A307" />
      </div>
      <div className="col-md-6">
        <label className="stg-label">สอบกลางภาค</label>
        <input type="text" name="midterm_exam" className="form-control" value={form.midterm_exam || ""} onChange={handleInput} placeholder="06/08/26 13:00-15:00" />
      </div>
      <div className="col-md-6">
        <label className="stg-label">สอบปลายภาค</label>
        <input type="text" name="final_exam" className="form-control" value={form.final_exam || ""} onChange={handleInput} placeholder="08/10/26 13:00-15:00" />
      </div>
    </div>
  );
};

// แสดงหัวกลุ่มเรียนเป็นข้อความอ่านอย่างเดียว (ใช้ในหน้าตรวจสอบก่อนนำเข้า)
const SectionSummary = ({ form }) => {
  const items = [
    ["ภาคการศึกษา", form.semester && form.academic_year ? `${form.semester}/${form.academic_year}` : ""],
    ["วิทยาเขต", form.campus],
    ["ระดับ / ประเภทนักศึกษา", form.program_type],
    ["รหัสวิชา", form.course_code],
    ["ชื่อวิชา", form.course_name],
    ["หน่วยกิต", form.credits],
    ["Sec", form.section_no],
    ["ผู้สอน", form.instructor_name],
    ["วันเวลาเรียน / ห้อง", form.class_schedule],
    ["สอบกลางภาค", form.midterm_exam],
    ["สอบปลายภาค", form.final_exam],
  ];

  return (
    <dl className="cmru-summary-grid">
      {items.map(([label, value]) => (
        <div key={label} className={label === "ชื่อวิชา" ? "is-wide" : ""}>
          <dt>{label}</dt>
          <dd>{value || "-"}</dd>
        </div>
      ))}
    </dl>
  );
};

const SectionModal = ({ show, onHide, editData, onSave }) => {
  const [form, setForm] = useState(sectionFormDefaults);

  useEffect(() => {
    setForm({ ...sectionFormDefaults, ...Object.fromEntries(Object.entries(editData || {}).map(([key, value]) => [key, value ?? ""])) });
  }, [editData, show]);

  return (
    <Modal animation={modalAnimation} show={show} onHide={onHide} centered size="lg" dialogClassName="stg-modal cmru-modal modal-dialog-scrollable">
      <ModalHeader
        eyebrow="Class Section"
        title="แก้ไขข้อมูลกลุ่มเรียน"
        subtitle="กลุ่มเรียนไม่ซ้ำกันด้วย ปีการศึกษา + ภาค + รหัสวิชา + Sec"
      />
      <Modal.Body>
        <div className="stg-section">
          <SectionFields form={form} onChange={(name, value) => setForm((current) => ({ ...current, [name]: value }))} />
          <div className="mt-3">
            <label className="stg-label">หมายเหตุ</label>
            <textarea className="form-control" rows={2} value={form.note || ""} onChange={(event) => setForm((current) => ({ ...current, note: event.target.value }))} />
          </div>
        </div>
      </Modal.Body>
      <ModalFooter onHide={onHide} onSave={() => onSave(form)} />
    </Modal>
  );
};

const StudentModal = ({ show, onHide, editData, onSave }) => {
  const [form, setForm] = useState(studentFormDefaults);

  useEffect(() => {
    setForm({
      ...studentFormDefaults,
      enrollment_uid: editData?.uid || "",
      seq_no: editData?.seq_no ?? "",
      student_code: editData?.student?.student_code || "",
      prefix_name: editData?.student?.prefix_name || "",
      first_name: editData?.student?.first_name || "",
      last_name: editData?.student?.last_name || "",
      student_group: editData?.student?.student_group || editData?.presetGroup || "",
      nickname: editData?.student?.nickname || "",
      note: editData?.note || "",
    });
  }, [editData, show]);

  const handleInput = (event) => setForm((current) => ({ ...current, [event.target.name]: event.target.value }));

  return (
    <Modal animation={modalAnimation} show={show} onHide={onHide} centered dialogClassName="stg-modal cmru-modal">
      <ModalHeader
        eyebrow="Student"
        title={editData?.uid ? "แก้ไขข้อมูลนักศึกษา" : "เพิ่มนักศึกษาเข้ากลุ่มเรียน"}
        subtitle={editData?.uid ? "" : "ถ้ารหัสนี้เคยมีในระบบแล้ว จะใช้ข้อมูลนักศึกษาคนเดิมและอัปเดตชื่อตามที่กรอก"}
      />
      <Modal.Body>
        <div className="row g-3">
          <div className="col-4">
            <label className="stg-label">เลขที่</label>
            <input type="number" min="1" name="seq_no" className="form-control" value={form.seq_no} onChange={handleInput} placeholder="ต่อท้าย" />
          </div>
          <div className="col-8">
            <label className="stg-label">รหัสประจำตัว</label>
            <input type="text" inputMode="numeric" name="student_code" className="form-control" value={form.student_code} onChange={handleInput} placeholder="68151801" />
          </div>
          <div className="col-md-4">
            <label className="stg-label">คำนำหน้า</label>
            <Select className="form-control" value={form.prefix_name} options={prefixOptions} placeholder="เลือก" onChange={(value) => setForm((current) => ({ ...current, prefix_name: value }))} />
          </div>
          <div className="col-md-4">
            <label className="stg-label">ชื่อ</label>
            <input type="text" name="first_name" className="form-control" value={form.first_name} onChange={handleInput} />
          </div>
          <div className="col-md-4">
            <label className="stg-label">นามสกุล</label>
            <input type="text" name="last_name" className="form-control" value={form.last_name} onChange={handleInput} />
          </div>
          <div className="col-md-4">
            <label className="stg-label">ชื่อเล่น</label>
            <input type="text" name="nickname" className="form-control" value={form.nickname} onChange={handleInput} />
          </div>
          <div className="col-md-8">
            <label className="stg-label">หมู่เรียน</label>
            <input type="text" name="student_group" className="form-control" value={form.student_group} onChange={handleInput} placeholder="ท 68.ค.บ.4.1" />
          </div>
          <div className="col-12">
            <label className="stg-label">หมายเหตุ</label>
            <textarea name="note" className="form-control" rows={2} value={form.note} onChange={handleInput} placeholder="เช่น ลงทะเบียนเพิ่มภายหลัง" />
          </div>
        </div>
      </Modal.Body>
      <ModalFooter onHide={onHide} onSave={() => onSave(form)} />
    </Modal>
  );
};

const importRowStatus = {
  added: { label: "เพิ่มใหม่", className: "stg-pill--success" },
  updated: { label: "อัปเดตข้อมูล", className: "stg-pill--gold" },
  unchanged: { label: "มีอยู่แล้ว", className: "stg-pill--muted" },
};

// ตรวจสอบข้อมูลที่อ่านได้จากไฟล์ก่อนบันทึกจริง แก้หัวกลุ่มเรียนได้ถ้าอ่านมาผิด
// ระหว่างนี้ยังไม่มีอะไรถูกบันทึก: ผลเทียบกับข้อมูลในระบบมาจากเส้น import/preview ซึ่ง rollback ทุกครั้ง
const ImportModal = ({ show, onHide, parsed, onConfirm }) => {
  const [form, setForm] = useState(sectionFormDefaults);
  // status: idle | loading | ready | failed
  const [preview, setPreview] = useState({ status: "idle", data: null });

  useEffect(() => {
    setForm({ ...sectionFormDefaults, ...(parsed?.section || {}) });
    setPreview({ status: "idle", data: null });
  }, [parsed, show]);

  const students = parsed?.students || [];
  const { academic_year, semester, course_code, section_no, course_name } = form;

  // เทียบกับข้อมูลในระบบใหม่ทุกครั้งที่ key ของกลุ่มเรียนเปลี่ยน (แก้ Sec แล้วอาจกลายเป็นคนละกลุ่ม)
  // หน่วงไว้ครึ่งวินาทีไม่ให้ยิงทุกตัวอักษรที่พิมพ์
  useEffect(() => {
    if (!show || !parsed?.students?.length) return undefined;
    if (validateSection(form)) {
      setPreview({ status: "idle", data: null });
      return undefined;
    }

    let isCurrent = true;
    setPreview((current) => ({ ...current, status: "loading" }));
    const timer = setTimeout(async () => {
      const result = await POST(`${API}/import/preview`, { section: toSectionBody(form), students: parsed.students, source_file_name: parsed.fileName }, null, { silent: true });
      if (!isCurrent) return;
      setPreview(result?.status === true ? { status: "ready", data: result.data } : { status: "failed", data: null });
    }, 500);

    return () => {
      isCurrent = false;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, parsed, academic_year, semester, course_code, section_no, course_name]);

  // ปกติแสดงหัวกลุ่มเรียนเป็นข้อความ จะเปิดช่องกรอกเฉพาะเมื่ออ่านค่าที่จำเป็นจากไฟล์ไม่ได้ (ไม่งั้นนำเข้าต่อไม่ได้เลย)
  const needsManualInput = !!parsed?.section && !!validateSection(parsed.section);

  const summary = preview.data;
  const statusByCode = Object.fromEntries((summary?.rows || []).map((row) => [row.student_code, row.status]));

  return (
    <Modal animation={modalAnimation} show={show} onHide={onHide} centered size="xl" dialogClassName="stg-modal cmru-modal modal-dialog-scrollable">
      <ModalHeader eyebrow="Import" title="ตรวจสอบข้อมูลก่อนนำเข้า" subtitle={`${parsed?.fileName || ""} · พบรายชื่อ ${students.length} คน`} />
      <Modal.Body>
        {(parsed?.warnings || []).length > 0 && (
          <div className="cmru-notice mb-3">
            <i className="fa-solid fa-triangle-exclamation" />
            <div>{parsed.warnings.map((message) => <div key={message}>{message}</div>)}</div>
          </div>
        )}

        {/* สิ่งที่จะเกิดขึ้นเมื่อกดยืนยัน เทียบกับข้อมูลที่มีอยู่ในระบบตอนนี้ */}
        <div className="cmru-import-summary mb-3">
          {preview.status === "ready" && summary ? (
            <>
              <div className="cmru-import-summary-head">
                <i className={summary.is_new_section ? "fa-solid fa-circle-plus" : "fa-solid fa-rotate"} />
                <div>
                  <div className="stg-toggle-title">
                    {summary.is_new_section ? "จะสร้างกลุ่มเรียนใหม่" : "กลุ่มเรียนนี้มีอยู่แล้ว จะอัปเดตกลุ่มเดิม"}
                  </div>
                  <div className="stg-toggle-note">ยังไม่มีการบันทึกข้อมูล จนกว่าจะกดปุ่มนำเข้าด้านล่าง</div>
                </div>
              </div>
              <div className="cmru-import-counts">
                <div><strong>{summary.total_in_file}</strong><span>คนในไฟล์</span></div>
                <div className="is-added"><strong>{summary.enrollments_added}</strong><span>เพิ่มใหม่</span></div>
                <div className="is-updated"><strong>{(summary.rows || []).filter((row) => row.status === "updated").length}</strong><span>อัปเดตข้อมูล</span></div>
                <div><strong>{(summary.rows || []).filter((row) => row.status === "unchanged").length}</strong><span>มีอยู่แล้ว</span></div>
              </div>
              {summary.not_in_file?.length > 0 && (
                <div className="cmru-notice mt-3">
                  <i className="fa-solid fa-circle-info" />
                  <div>
                    มี {summary.not_in_file.length} คนอยู่ในกลุ่มเรียนนี้แต่ไม่อยู่ในไฟล์ ระบบจะ<strong>ไม่ลบ</strong>ให้:{" "}
                    {summary.not_in_file.map((item) => `${item.student_code}${item.name ? ` ${item.name}` : ""}`).join(", ")}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="cmru-import-summary-head">
              <i className={preview.status === "failed" ? "fa-solid fa-triangle-exclamation" : "fa-solid fa-magnifying-glass"} />
              <div>
                <div className="stg-toggle-title">
                  {preview.status === "loading" && "กำลังเทียบกับข้อมูลในระบบ..."}
                  {preview.status === "failed" && "เทียบกับข้อมูลในระบบไม่สำเร็จ"}
                  {preview.status === "idle" && "กรอกข้อมูลกลุ่มเรียนให้ครบเพื่อเทียบกับข้อมูลในระบบ"}
                </div>
                <div className="stg-toggle-note">
                  {preview.status === "failed" ? "ยังตรวจรายชื่อด้านล่างและกดนำเข้าได้ตามปกติ" : "ยังไม่มีการบันทึกข้อมูล จนกว่าจะกดปุ่มนำเข้าด้านล่าง"}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="stg-section">
          <div className="stg-section-head">
            <span className="stg-section-mark"><i className="fa-solid fa-book-open" /></span>
            <div>
              <h4 className="stg-section-title">ข้อมูลกลุ่มเรียน</h4>
              <p className="stg-section-desc">
                {needsManualInput
                  ? "อ่านหัวเอกสารได้ไม่ครบ กรุณากรอกช่องที่ขาดก่อนนำเข้า"
                  : "อ่านจากหัวเอกสาร แก้ไขภายหลังได้จากปุ่ม \"แก้ไขกลุ่มเรียน\" ในหน้ารายชื่อ"}
              </p>
            </div>
          </div>
          {needsManualInput ? (
            <SectionFields form={form} onChange={(name, value) => setForm((current) => ({ ...current, [name]: value }))} />
          ) : (
            <SectionSummary form={form} />
          )}
        </div>

        <div className="stg-section">
          <div className="stg-section-head">
            <span className="stg-section-mark"><i className="fa-solid fa-users" /></span>
            <div>
              <h4 className="stg-section-title">รายชื่อนักศึกษา {students.length} คน</h4>
              <p className="stg-section-desc">คอลัมน์สัปดาห์ในไฟล์ไม่ถูกนำเข้า</p>
            </div>
          </div>
          <div className="table-responsive stg-table-wrap" style={{ maxHeight: "40vh", overflowY: "auto" }}>
            <table className="table table-hover align-middle stg-table">
              <thead>
                <tr>
                  <th className="text-center">เลขที่</th>
                  <th>รหัสประจำตัว</th>
                  <th>คำนำหน้า</th>
                  <th>ชื่อ</th>
                  <th>นามสกุล</th>
                  <th>หมู่เรียน</th>
                  <th className="text-center">ผลเมื่อนำเข้า</th>
                </tr>
              </thead>
              <tbody>
                {students.map((item) => (
                  <tr key={`${item.seq_no}-${item.student_code}`}>
                    <td className="text-center">{item.seq_no}</td>
                    <td><span className="stg-code">{item.student_code}</span></td>
                    <td>{item.prefix_name || "-"}</td>
                    <td className="stg-cell-title">{item.first_name || "-"}</td>
                    <td>{item.last_name || "-"}</td>
                    <td>{item.student_group || "-"}</td>
                    <td className="text-center">
                      {importRowStatus[statusByCode[item.student_code]] ? (
                        <span className={`stg-pill stg-pill--plain ${importRowStatus[statusByCode[item.student_code]].className}`}>
                          {importRowStatus[statusByCode[item.student_code]].label}
                        </span>
                      ) : (
                        <span className="stg-cell-sub">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Modal.Body>
      <ModalFooter onHide={onHide} onSave={() => onConfirm(form)} saveLabel={`นำเข้า ${students.length} คน`} saveIcon="fa-solid fa-file-import" disabled={!students.length} />
    </Modal>
  );
};

export default function CmruClassLists() {
  const { title, description } = appModules["cmru_class_lists"];
  const { uid: sectionUid } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  // แท็บของหน้ากลุ่มเรียนเก็บไว้ใน URL (?tab=attendance / ?tab=scores) รีเฟรชหน้าแล้วยังอยู่แท็บเดิม
  const [searchParams, setSearchParams] = useSearchParams();
  const detailTab = ["attendance", "scores"].includes(searchParams.get("tab")) ? searchParams.get("tab") : "students";
  // แท็บเช็คชื่อ/คะแนนไม่บันทึกอัตโนมัติ ถ้ามีของค้างต้องถามก่อนพาออกจากแท็บ ไม่งั้นข้อมูลที่กรอกไว้หายเงียบ ๆ
  const [tabDirty, setTabDirty] = useState(false);
  const confirmLeaveTab = async () =>
    !tabDirty || (await alert("", "warning", "ยังไม่ได้บันทึกข้อมูล", "มีข้อมูลที่ยังไม่ได้บันทึก ออกจากแท็บนี้โดยไม่บันทึก ?", "ออกโดยไม่บันทึก", "อยู่แท็บนี้ต่อ"));

  const [sections, setSections] = useState([]);
  const [search, setSearch] = useState("");
  const [termFilter, setTermFilter] = useState("");
  const [detail, setDetail] = useState(null);
  const [studentSearch, setStudentSearch] = useState("");

  const [showSectionModal, setShowSectionModal] = useState(false);
  const [editSectionData, setEditSectionData] = useState(null);
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [editStudentData, setEditStudentData] = useState(null);
  const [parsedImport, setParsedImport] = useState(null);

  const getSections = async () => {
    loading(true);
    const result = await GET(API, { page: 0 });
    if (result?.status) setSections(result.data || []);
    loading(false);
  };

  const getDetail = async (uid) => {
    loading(true);
    const result = await GET(API, { uid });
    if (result?.status) {
      setDetail(result.data);
    } else {
      navigate(LIST_PATH, { replace: true });
    }
    loading(false);
  };

  useEffect(() => {
    setStudentSearch("");
    if (sectionUid) {
      setDetail(null);
      getDetail(sectionUid);
    } else {
      getSections();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionUid]);

  const reload = () => (sectionUid ? getDetail(sectionUid) : getSections());

  /* ------------------------------ นำเข้าไฟล์ ------------------------------ */

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    // ล้างค่า input ทุกครั้ง ไม่งั้นเลือกไฟล์เดิมซ้ำแล้ว onChange ไม่ทำงาน
    event.target.value = "";
    if (!file) return;

    loading(true);
    try {
      const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: "" });
      const parsed = parseClassListRows(rows);
      loading(false);

      if (parsed.error) {
        await alert("", "warning", "อ่านไฟล์ไม่ได้", parsed.error);
        return;
      }
      setParsedImport({ ...parsed, fileName: file.name });
    } catch (error) {
      loading(false);
      console.error("Read class list file error:", error);
      await alert("", "error", "ผิดพลาด", "เปิดไฟล์ไม่สำเร็จ กรุณาใช้ไฟล์ .xlsx ที่ส่งออกจากระบบของมหาวิทยาลัย");
    }
  };

  const confirmImport = async (sectionForm) => {
    const invalid = validateSection(sectionForm);
    if (invalid) {
      await alert("", "warning", "แจ้งเตือน", invalid);
      return;
    }
    if (!(await alert("", "info", "ยืนยันการนำเข้า", `นำเข้ารายชื่อ ${parsedImport.students.length} คน เข้ากลุ่มเรียน ${sectionForm.course_code} Sec ${sectionForm.section_no} ?`, "นำเข้า", "ยกเลิก"))) return;

    // payload เป็น JSON ก้อนเล็ก ส่งขึ้นเสร็จแทบทันที เวลาที่รอจริงคือ server บันทึกข้อมูล ซึ่งวัด % ไม่ได้
    // แถบจึงค่อย ๆ ไต่ไปรอที่ 95% แล้วจบที่ 100% เมื่อ server ตอบกลับ ค้างให้เห็นครู่หนึ่งก่อนขึ้น alert
    let percent = 0;
    loading(true, percent);
    const progressTimer = setInterval(() => {
      percent = Math.min(95, percent + Math.max(1, (95 - percent) * 0.08));
      loading(true, percent);
    }, 120);

    const result = await POST(`${API}/import`, {
      section: toSectionBody(sectionForm),
      students: parsedImport.students,
      source_file_name: parsedImport.fileName,
    });
    clearInterval(progressTimer);

    if (result?.status === true) {
      loading(true, 100);
      await new Promise((resolve) => setTimeout(resolve, 450));
    }
    loading(false);

    if (result?.status !== true) {
      alert("", "warning", "แจ้งเตือน", result?.message || "นำเข้าไม่สำเร็จ กรุณาลองใหม่อีกครั้ง");
      return;
    }

    const summary = result.data || {};
    const lines = [
      summary.is_new_section ? "สร้างกลุ่มเรียนใหม่" : "อัปเดตกลุ่มเรียนเดิม",
      `เพิ่มเข้ากลุ่ม ${summary.enrollments_added || 0} คน · ไม่เปลี่ยนแปลง ${summary.unchanged || 0} คน`,
      summary.students_updated ? `อัปเดตชื่อ/หมู่เรียน ${summary.students_updated} คน` : "",
      summary.not_in_file?.length ? `มี ${summary.not_in_file.length} คนอยู่ในระบบแต่ไม่อยู่ในไฟล์ (ระบบไม่ลบให้): ${summary.not_in_file.map((item) => item.student_code).join(", ")}` : "",
    ].filter(Boolean);

    setParsedImport(null);
    await alert("", "success", "นำเข้าสำเร็จ", lines.join("\n"));

    // นำเข้าเสร็จกลับไปหน้ารายการกลุ่มเรียน (รายชื่อนักศึกษาลงทะเบียน) เสมอ
    if (sectionUid) navigate(LIST_PATH);
    else getSections();
  };

  /* ------------------------------ กลุ่มเรียน ------------------------------ */

  const saveSection = async (form) => {
    const invalid = validateSection(form);
    if (invalid) {
      await alert("", "warning", "แจ้งเตือน", invalid);
      return;
    }
    if (!(await alert("", "info", "ยืนยัน", "ยืนยันการบันทึกข้อมูลหรือไม่?", "ยืนยัน", "ยกเลิก"))) return;

    // กลุ่มเรียนสร้างได้ทางเดียวคือนำเข้าไฟล์ ตรงนี้จึงมีแค่แก้ไข
    loading(true);
    const result = await PUT(`${API}/${form.uid}`, toSectionBody(form));
    loading(false);

    if (result?.status === true) {
      alert("", "success", "สำเร็จ", "ทำรายการสำเร็จแล้ว");
      setShowSectionModal(false);
      setEditSectionData(null);
      reload();
    } else {
      alert("", "warning", "แจ้งเตือน", result?.message || "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง");
    }
  };

  const deleteSection = async (item) => {
    if (!(await alert("", "warning", "ลบกลุ่มเรียน", `ต้องการลบ ${item.course_code} Sec ${item.section_no} พร้อมรายชื่อ ${item.student_count || 0} คนในกลุ่มนี้ ?`, "ลบ", "ยกเลิก"))) return;

    loading(true);
    const result = await DELETE(`${API}/${item.uid}`);
    loading(false);

    if (result?.status === true) {
      alert("", "success", "สำเร็จ", "ทำรายการสำเร็จแล้ว");
      if (sectionUid) navigate(LIST_PATH);
      else getSections();
    } else {
      alert("", "warning", "แจ้งเตือน", result?.message || "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง");
    }
  };

  /* ------------------------------ นักศึกษา ------------------------------ */

  const saveStudent = async (form) => {
    if (!form.student_code.trim() || !form.first_name.trim()) {
      await alert("", "warning", "แจ้งเตือน", "กรุณากรอกรหัสประจำตัวและชื่อ");
      return;
    }
    if (!(await alert("", "info", "ยืนยัน", "ยืนยันการบันทึกข้อมูลหรือไม่?", "ยืนยัน", "ยกเลิก"))) return;

    loading(true);
    const body = {
      student_code: form.student_code.trim(),
      prefix_name: form.prefix_name || "",
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      student_group: form.student_group.trim(),
      nickname: form.nickname.trim(),
      note: form.note.trim(),
      ...(String(form.seq_no).trim() !== "" ? { seq_no: Number(form.seq_no) } : {}),
    };
    const result = form.enrollment_uid
      ? await PUT(`${API}/${sectionUid}/students/${form.enrollment_uid}`, body)
      : await POST(`${API}/${sectionUid}/students`, body);
    loading(false);

    if (result?.status === true) {
      alert("", "success", "สำเร็จ", "ทำรายการสำเร็จแล้ว");
      setShowStudentModal(false);
      setEditStudentData(null);
      getDetail(sectionUid);
    } else {
      alert("", "warning", "แจ้งเตือน", result?.message || "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง");
    }
  };

  const deleteStudent = async (item) => {
    if (!(await alert("", "warning", "นำออกจากกลุ่มเรียน", `ต้องการนำ ${getStudentName(item.student)} ออกจากกลุ่มเรียนนี้ ?`, "นำออก", "ยกเลิก"))) return;

    loading(true);
    const result = await DELETE(`${API}/${sectionUid}/students/${item.uid}`);
    loading(false);

    if (result?.status === true) {
      alert("", "success", "สำเร็จ", "ทำรายการสำเร็จแล้ว");
      getDetail(sectionUid);
    } else {
      alert("", "warning", "แจ้งเตือน", result?.message || "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง");
    }
  };

  /* ------------------------------ ข้อมูลสำหรับแสดงผล ------------------------------ */

  const termOptions = [...new Set(sections.map((item) => `${item.semester}/${item.academic_year}`))].map((value) => ({ value, label: `ภาคการศึกษา ${value}` }));

  const keyword = search.trim().toLowerCase();
  const visibleSections = sections.filter((item) => {
    if (termFilter && `${item.semester}/${item.academic_year}` !== termFilter) return false;
    if (!keyword) return true;
    return [item.course_code, item.course_name, item.instructor_name, item.section_no].filter(Boolean).some((field) => String(field).toLowerCase().includes(keyword));
  });

  const students = detail?.students || [];
  const studentKeyword = studentSearch.trim().toLowerCase();
  const visibleStudents = studentKeyword
    ? students.filter((item) => `${item.student?.student_code} ${getStudentName(item.student)} ${item.student?.student_group || ""} ${item.student?.nickname || ""}`.toLowerCase().includes(studentKeyword))
    : students;
  const studentGroups = [...new Set(students.map((item) => item.student?.student_group).filter(Boolean))];
  const countGender = (gender) => students.filter((item) => getGender(item.student) === gender).length;
  const studentStats = [
    ["green", "fa-solid fa-users", students.length, "นักศึกษาทั้งหมด"],
    ["blue", "fa-solid fa-mars", countGender("male"), "ชาย"],
    ["pink", "fa-solid fa-venus", countGender("female"), "หญิง"],
  ];

  const importButton = (
    <button type="button" className="btn stg-btn stg-btn--primary" onClick={() => fileInputRef.current?.click()}>
      <i className="fa-solid fa-file-import" />นำเข้าไฟล์ Excel
    </button>
  );

  return (
    <CmruShell title={title} description={description}>
      <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="d-none" onChange={handleFileChange} />

      <div className="stg cmru">
        {!sectionUid ? (
          <div className="stg-panel">
            <div className="stg-panel-head">
              <div className="stg-panel-intro">
                <span className="stg-panel-icon"><i className="fa-solid fa-clipboard-list" /></span>
                <div style={{ minWidth: 0 }}>
                  <p className="stg-eyebrow">Class Lists</p>
                  <h3 className="stg-panel-title">รายชื่อนักศึกษาลงทะเบียน</h3>
                  <p className="stg-panel-desc">นำเข้าใบรายชื่อจากไฟล์ Excel ของมหาวิทยาลัย 1 ไฟล์ = 1 กลุ่มเรียน</p>
                </div>
              </div>
              <div className="stg-panel-actions">
                {importButton}
              </div>
            </div>

            <div className="stg-panel-body">
              <div className="row g-2 mb-3">
                <div className="col-md-8">
                  <div className="stg-search">
                    <i className="fa-solid fa-magnifying-glass" />
                    <input type="text" className="form-control" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="ค้นหารหัสวิชา ชื่อวิชา หรือผู้สอน..." />
                  </div>
                </div>
                <div className="col-md-4">
                  <Select className="form-control" value={termFilter} options={termOptions} placeholder="ทุกภาคการศึกษา" onChange={(value) => setTermFilter(value)} />
                </div>
              </div>

              {/* มือถือก็แสดงเป็นตาราง (เลื่อนแนวนอนได้) เหมือนตารางรายชื่อนักศึกษา */}
              <div className="table-responsive stg-table-wrap">
                <table className="table table-hover align-middle stg-table stg-table--compact cmru-lux-table">
                  <thead>
                    <tr>
                      <th>รหัสวิชา</th>
                      <th>ชื่อวิชา</th>
                      <th className="text-center">Sec</th>
                      <th className="text-center">ภาค</th>
                      <th>ผู้สอน</th>
                      <th>วันเวลาเรียน</th>
                      <th className="text-center">นักศึกษา</th>
                      <th className="text-center">จัดการ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleSections.length > 0 ? (
                      visibleSections.map((item) => {
                        const schedule = splitSchedule(item.class_schedule);
                        const instructor = getInstructorBadge(item.instructor_name);
                        const studentCount = Number(item.student_count) || 0;
                        const courseNote = [item.credits ? `${item.credits} หน่วยกิต` : "", item.program_type].filter(Boolean).join(" · ");

                        return (
                        <tr key={item.uid} className="cmru-row-link" onClick={() => navigate(`${LIST_PATH}/${item.uid}`)}>
                          <td data-label="รหัสวิชา"><span className="cmru-course-code">{item.course_code}</span></td>
                          {/* ทุกแถวสูงบรรทัดเดียว ชื่อวิชายาวเกินจะตัดเป็น ... ชี้ค้างไว้เพื่อดูชื่อเต็ม + หน่วยกิต/ประเภทนักศึกษา */}
                          <td data-label="ชื่อวิชา">
                            <span className="stg-cell-title cmru-course-name" title={[item.course_name, courseNote].filter(Boolean).join("\n")}>{item.course_name}</span>
                          </td>
                          <td data-label="Sec" className="text-center"><span className="cmru-sec">{item.section_no}</span></td>
                          <td data-label="ภาค" className="text-center text-nowrap">
                            <span className={`cmru-term cmru-term--${item.semester}`}>{item.semester}/{item.academic_year}</span>
                          </td>
                          <td data-label="ผู้สอน" className="text-nowrap">
                            {item.instructor_name ? (
                              <span className="cmru-person">
                                <span className={`cmru-person-mark cmru-person-mark--${instructor.tone}`}>{instructor.initial}</span>
                                {item.instructor_name}
                              </span>
                            ) : "-"}
                          </td>
                          <td data-label="วันเวลาเรียน" className="text-nowrap">
                            {item.class_schedule ? (
                              <span className="cmru-schedule">
                                {schedule.day ? <span className={`cmru-day cmru-day--${schedule.tone}`}>{schedule.day}.</span> : null}
                                <span className="cmru-schedule-time">{schedule.time}</span>
                                {schedule.room ? <span className="cmru-room"><i className="fa-solid fa-location-dot" />{schedule.room}</span> : null}
                              </span>
                            ) : "-"}
                          </td>
                          <td data-label="นักศึกษา" className="text-center">
                            <span className={`cmru-count ${studentCount ? "" : "is-empty"}`}>
                              <i className="fa-solid fa-user-group" /><strong>{studentCount}</strong>คน
                            </span>
                          </td>
                          <td data-label="จัดการ" className="text-center" onClick={(event) => event.stopPropagation()}>
                            <div className="d-flex justify-content-center gap-2">
                              <button type="button" onClick={() => navigate(`${LIST_PATH}/${item.uid}`)} className="stg-icon-btn" title="ดูรายชื่อ" aria-label="ดูรายชื่อ">
                                <i className="fa-solid fa-users" />
                              </button>
                              <button type="button" onClick={() => { setEditSectionData(item); setShowSectionModal(true); }} className="stg-icon-btn" title="แก้ไขกลุ่มเรียน" aria-label="แก้ไขกลุ่มเรียน">
                                <i className="fa-solid fa-pen" />
                              </button>
                              <button type="button" onClick={() => deleteSection(item)} className="stg-icon-btn stg-icon-btn--danger" title="ลบ" aria-label="ลบ">
                                <i className="fa-solid fa-trash" />
                              </button>
                            </div>
                          </td>
                        </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="8" className="stg-empty">
                          <i className="fa-regular fa-folder-open" />
                          {sections.length ? "ไม่พบกลุ่มเรียนที่ค้นหา" : "ยังไม่มีกลุ่มเรียน เริ่มจากกด \"นำเข้าไฟล์ Excel\""}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {sections.length > 0 && (
                <p className="cmru-table-foot">
                  แสดง <strong>{visibleSections.length}</strong> จาก {sections.length} กลุ่มเรียน · คลิกที่แถวเพื่อเปิดรายชื่อนักศึกษา
                </p>
              )}
            </div>
          </div>
        ) : (
          <>
            <button type="button" className="btn stg-btn stg-btn--ghost mb-3" onClick={async () => { if (await confirmLeaveTab()) navigate(LIST_PATH); }}>
              <i className="fa-solid fa-arrow-left" />กลับไปรายการกลุ่มเรียน
            </button>

            {detail && (
              <>
                {/* แท็บเช็คชื่อ/คะแนนย่อหัววิชาลง ให้เหลือที่กับตารางมากที่สุด (สำคัญบนมือถือตอนเช็คชื่อในห้อง) */}
                <div className={`cmru-course mb-3 ${detailTab !== "students" ? "is-compact" : ""}`}>
                  <div className="cmru-course-main">
                    <div className="cmru-course-chips">
                      <span className="cmru-chip cmru-chip--code">{detail.course_code}</span>
                      <span className="cmru-chip">Sec {detail.section_no}</span>
                      <span className="cmru-chip">ภาคการศึกษา {detail.semester}/{detail.academic_year}</span>
                      {detail.credits ? <span className="cmru-chip">{detail.credits} หน่วยกิต</span> : null}
                    </div>
                    <h1 className="cmru-course-title">{detail.course_name}</h1>
                    <div className="cmru-course-meta">
                      {detail.instructor_name ? <span><i className="fa-solid fa-chalkboard-user" />{detail.instructor_name}</span> : null}
                      {detail.program_type ? <span><i className="fa-solid fa-user-graduate" />{detail.program_type}</span> : null}
                      {detail.campus ? <span><i className="fa-solid fa-location-dot" />วิทยาเขต{detail.campus}</span> : null}
                    </div>
                  </div>

                  <div className="cmru-course-count">
                    <strong>{students.length}</strong>
                    <span>นักศึกษา</span>
                  </div>

                  <div className="cmru-course-times">
                    {[
                      ["fa-regular fa-clock", "วันเวลาเรียน / ห้อง", detail.class_schedule],
                      ["fa-solid fa-pen-to-square", "สอบกลางภาค", detail.midterm_exam],
                      ["fa-solid fa-flag-checkered", "สอบปลายภาค", detail.final_exam],
                    ].map(([icon, label, value]) => (
                      <div className="cmru-course-time" key={label}>
                        <i className={icon} />
                        <div>
                          <div className="cmru-course-time-label">{label}</div>
                          <div className="cmru-course-time-value">{value || "-"}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="stg-segment mb-3" role="tablist" aria-label="ข้อมูลของกลุ่มเรียน">
                  {[
                    ["students", "fa-solid fa-users", "รายชื่อ"],
                    ["attendance", "fa-solid fa-clipboard-check", "เช็คชื่อ"],
                    ["scores", "fa-solid fa-star", "คะแนน"],
                  ].map(([key, icon, label]) => (
                    <button
                      key={key}
                      type="button"
                      role="tab"
                      aria-selected={detailTab === key}
                      onClick={async () => {
                        if (key !== detailTab && (await confirmLeaveTab())) setSearchParams(key === "students" ? {} : { tab: key }, { replace: true });
                      }}
                      className={`stg-segment-item ${detailTab === key ? "is-active" : ""}`}
                    >
                      <i className={icon} />{label}
                    </button>
                  ))}
                </div>

                {detailTab === "attendance" && <AttendanceTab detail={detail} setDetail={setDetail} reload={() => getDetail(sectionUid)} onDirtyChange={setTabDirty} />}
                {detailTab === "scores" && <ScoreTab detail={detail} setDetail={setDetail} reload={() => getDetail(sectionUid)} onDirtyChange={setTabDirty} />}

                {detailTab === "students" && (
                <div className="stg-panel">
                  <div className="stg-panel-head">
                    <div className="stg-panel-intro">
                      <span className="stg-panel-icon"><i className="fa-solid fa-users" /></span>
                      <div style={{ minWidth: 0 }}>
                        <p className="stg-eyebrow">Students</p>
                        <h3 className="stg-panel-title">รายชื่อนักศึกษา</h3>
                        <p className="stg-panel-desc">
                          {studentGroups.length ? `หมู่เรียน ${studentGroups.join(", ")}` : "ยังไม่มีรายชื่อ"}
                          {detail.last_imported_at ? ` · นำเข้าล่าสุด ${format_date(detail.last_imported_at, "dd/mm/yyyy")}` : ""}
                        </p>
                      </div>
                    </div>
                    <div className="stg-panel-actions">
                      <button type="button" className="btn stg-btn stg-btn--ghost" onClick={() => { setEditSectionData(detail); setShowSectionModal(true); }}>
                        <i className="fa-solid fa-pen" />แก้ไขกลุ่มเรียน
                      </button>
                      <button type="button" className="btn stg-btn stg-btn--ghost" onClick={() => { setEditStudentData({ presetGroup: studentGroups[0] || "" }); setShowStudentModal(true); }}>
                        <i className="fa-solid fa-user-plus" />เพิ่มนักศึกษา
                      </button>
                    </div>
                  </div>

                  <div className="stg-panel-body">
                    <KpiStrip items={studentStats} compact />

                    <div className="stg-search mb-3">
                      <i className="fa-solid fa-magnifying-glass" />
                      <input type="text" className="form-control" value={studentSearch} onChange={(event) => setStudentSearch(event.target.value)} placeholder="ค้นหารหัสประจำตัว ชื่อ ชื่อเล่น หรือหมู่เรียน..." />
                    </div>

                    {/* รายชื่อยาว: มือถือก็แสดงเป็นตาราง (เลื่อนแนวนอนได้) ไม่แปลงเป็นการ์ด จะได้เห็นหลายคนต่อจอ */}
                    <div className="table-responsive stg-table-wrap">
                      <table className="table table-hover align-middle stg-table stg-table--compact cmru-lux-table">
                        <thead>
                          <tr>
                            <th className="text-center">เลขที่</th>
                            <th>รหัสประจำตัว</th>
                            <th>ชื่อ - นามสกุล</th>
                            <th>หมู่เรียน</th>
                            <th>ชื่อเล่น</th>
                            <th className="text-center">ที่มา</th>
                            <th className="text-center">จัดการ</th>
                          </tr>
                        </thead>
                        <tbody>
                          {visibleStudents.length > 0 ? (
                            visibleStudents.map((item, index) => (
                              <tr key={item.uid}>
                                <td data-label="เลขที่" className="text-center"><span className="cmru-seq">{item.seq_no ?? "-"}</span></td>
                                <td data-label="รหัสประจำตัว"><span className="cmru-student-code">{item.student?.student_code || "-"}</span></td>
                                <td data-label="ชื่อ - นามสกุล" className="text-nowrap">
                                  <span className="cmru-person">
                                    <span className={`cmru-gender cmru-gender--${getGender(item.student)}`}><i className={genderIcons[getGender(item.student)]} /></span>
                                    <span>{getStudentName(item.student)}</span>
                                    {item.note ? <span className="cmru-note" title={item.note}><i className="fa-solid fa-note-sticky" />{item.note}</span> : null}
                                  </span>
                                </td>
                                <td data-label="หมู่เรียน" className="text-nowrap">
                                  {item.student?.student_group ? (
                                    <span className={`cmru-group cmru-group--${studentGroups.indexOf(item.student.student_group) % 5}`}>{item.student.student_group}</span>
                                  ) : "-"}
                                </td>
                                <td data-label="ชื่อเล่น" className="text-nowrap cmru-nick-cell">
                                  <NicknameCell
                                    sectionUid={sectionUid}
                                    enrollment={item}
                                    rowIndex={index}
                                    onSaved={(nickname) => setDetail((current) => ({
                                      ...current,
                                      students: current.students.map((row) => (row.uid === item.uid ? { ...row, student: { ...row.student, nickname } } : row)),
                                    }))}
                                  />
                                </td>
                                <td data-label="ที่มา" className="text-center">
                                  <span className={`stg-pill stg-pill--plain ${item.source === "manual" ? "stg-pill--gold" : "stg-pill--muted"}`}>
                                    <i className={item.source === "manual" ? "fa-solid fa-user-pen" : "fa-regular fa-file-excel"} />
                                    {item.source === "manual" ? "เพิ่มเอง" : "จากไฟล์"}
                                  </span>
                                </td>
                                <td data-label="จัดการ" className="text-center">
                                  <div className="d-flex justify-content-center gap-2">
                                    <button type="button" onClick={() => { setEditStudentData(item); setShowStudentModal(true); }} className="stg-icon-btn" title="แก้ไข" aria-label="แก้ไข">
                                      <i className="fa-solid fa-pen" />
                                    </button>
                                    <button type="button" onClick={() => deleteStudent(item)} className="stg-icon-btn stg-icon-btn--danger" title="นำออกจากกลุ่มเรียน" aria-label="นำออกจากกลุ่มเรียน">
                                      <i className="fa-solid fa-user-minus" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="8" className="stg-empty">
                                <i className="fa-regular fa-folder-open" />
                                {students.length ? "ไม่พบนักศึกษาที่ค้นหา" : "ยังไม่มีรายชื่อในกลุ่มเรียนนี้"}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      <SectionModal show={showSectionModal} onHide={() => { setShowSectionModal(false); setEditSectionData(null); }} editData={editSectionData} onSave={saveSection} />
      <StudentModal show={showStudentModal} onHide={() => { setShowStudentModal(false); setEditStudentData(null); }} editData={editStudentData} onSave={saveStudent} />
      <ImportModal show={!!parsedImport} onHide={() => setParsedImport(null)} parsed={parsedImport} onConfirm={confirmImport} />
    </CmruShell>
  );
}
